import { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import {
  Loader2,
  AlertCircle,
  Hotel,
  Newspaper,
  Video as VideoIcon,
  ExternalLink,
  Share2,
  MousePointerClick,
  ArrowLeft,
} from "lucide-react";

interface ClickEntry {
  tripId?: string | null;
  itemId?: string | null;
  kind: string;
  targetHost?: string;
  targetUrl?: string;
  ts: number;
}

// Model usage, written per call by api/_llm-usage.js and summed over the
// last seven days by api/stats.js.
interface LlmRoute {
  calls: number;
  iterations: number;
  input: number;
  cache_write: number;
  cache_read: number;
  output: number;
  web_searches: number;
  usd: number;
}

interface LlmUsage {
  since: string;
  routes: Record<string, LlmRoute>;
  totalUsd: number;
  days: { day: string; usd: number }[];
  concierge: {
    messages: number;
    medianUsd: number | null;
    p90Usd: number | null;
    cacheReadShare: number | null;
  };
}

interface StatsPayload {
  clicks: {
    total: number;
    byKind: Record<string, number>;
  };
  shares: {
    total: number;
  };
  recent: ClickEntry[];
  llm?: LlmUsage | null;
}

const LLM_ROUTE_LABEL: Record<string, string> = {
  concierge: "Concierge",
  extract: "Video place extraction",
  photo_pick: "Photo picker",
};

const usd = (v: number | null | undefined) =>
  v == null ? "n/a" : v < 0.01 && v > 0 ? `$${v.toFixed(4)}` : `$${v.toFixed(2)}`;
const tokens = (v: number) => v.toLocaleString("en-US");

const KIND_LABEL: Record<string, string> = {
  booking: "Booking",
  source: "Source",
  video: "Video opened",
  external: "Other",
};

const KIND_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  booking: Hotel,
  source: Newspaper,
  video: VideoIcon,
  external: ExternalLink,
};

const KIND_COLOR: Record<string, string> = {
  booking: "bg-emerald-500",
  source: "bg-blue-500",
  video: "bg-rose-500",
  external: "bg-amber-500",
};

const formatRelative = (ts: number): string => {
  const diffSec = (Date.now() - ts) / 1000;
  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
};

const Stats = () => {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState<StatsPayload | null>(null);
  const [status, setStatus] = useState<"loading" | "loaded" | "error">(
    "loading"
  );
  const [error, setError] = useState<string | null>(null);

  // Operator-only view. Not linked anywhere in the product; reachable at
  // /stats?admin=1. This is a soft gate (the data is just aggregate click
  // counts, not sensitive) to keep it out of the user-facing surface.
  // Casual visitors to /stats get bounced home.
  const isOperator = searchParams.get("admin") === "1";

  useEffect(() => {
    if (!isOperator) return;
    setStatus("loading");
    fetch("/api/stats")
      .then(async (res) => {
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(json?.error || `Stats failed (${res.status})`);
        }
        return json as StatsPayload;
      })
      .then((payload) => {
        setData(payload);
        setStatus("loaded");
      })
      .catch((err) => {
        setError(err?.message || "Couldn't load stats.");
        setStatus("error");
      });
  }, [isOperator]);

  // Bounce non-operators back home — /stats is not part of the product.
  if (!isOperator) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="container mx-auto px-4 py-10 max-w-4xl">
      <div className="mb-8">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors mb-4"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to SundayAtlas
        </Link>
        <h1 className="text-3xl md:text-4xl font-serif font-bold">
          Usage stats
        </h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl leading-relaxed">
          Real click-through data from the affiliate-tracking rail wired into
          every outbound link. No affiliate IDs are plugged in yet — this is
          the measurement side of the monetization story, ready to turn on.
        </p>
      </div>

      {status === "loading" && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading...
        </div>
      )}

      {status === "error" && (
        <div className="flex items-start gap-2 text-sm text-muted-foreground p-3 rounded-xl bg-muted/40 border border-border/60">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
          <span>{error}</span>
        </div>
      )}

      {status === "loaded" && data && (
        <StatsBody data={data} />
      )}
    </div>
  );
};

const StatsBody = ({ data }: { data: StatsPayload }) => {
  const totalClicks = data.clicks.total;
  const kinds = Object.keys(data.clicks.byKind);

  return (
    <div className="space-y-10">
      {/* Hero numbers */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <HeroNumber
          icon={<MousePointerClick className="h-4 w-4" />}
          label="Total clicks"
          value={totalClicks}
        />
        <HeroNumber
          icon={<Hotel className="h-4 w-4" />}
          label="Booking clicks"
          value={data.clicks.byKind.booking || 0}
        />
        <HeroNumber
          icon={<VideoIcon className="h-4 w-4" />}
          label="Videos opened"
          value={data.clicks.byKind.video || 0}
        />
        <HeroNumber
          icon={<Share2 className="h-4 w-4" />}
          label="Trips shared"
          value={data.shares.total}
        />
      </section>

      {/* Click mix */}
      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
          Click mix
        </h2>
        {totalClicks === 0 ? (
          <p className="text-sm text-muted-foreground">
            No clicks tracked yet.
          </p>
        ) : (
          <div className="space-y-2">
            {kinds.map((kind) => {
              const count = data.clicks.byKind[kind] || 0;
              const pct = totalClicks > 0 ? (count / totalClicks) * 100 : 0;
              const Icon = KIND_ICON[kind] || ExternalLink;
              return (
                <div key={kind} className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                      <Icon className="h-3.5 w-3.5" />
                      {KIND_LABEL[kind] || kind}
                    </span>
                    <span className="font-mono text-foreground">
                      {count}{" "}
                      <span className="text-muted-foreground">
                        ({pct.toFixed(0)}%)
                      </span>
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div
                      className={`h-full ${KIND_COLOR[kind] || "bg-gray-400"} transition-all`}
                      style={{ width: `${Math.max(2, pct)}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <LlmSpend llm={data.llm ?? null} />

      {/* Recent activity */}
      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
          Recent activity
        </h2>
        {data.recent.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No activity yet. Click an outbound link in a trip to see entries
            here.
          </p>
        ) : (
          <div className="border border-border/60 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">When</th>
                  <th className="text-left px-3 py-2 font-medium">Kind</th>
                  <th className="text-left px-3 py-2 font-medium">Target</th>
                </tr>
              </thead>
              <tbody>
                {data.recent.map((entry, i) => {
                  const Icon = KIND_ICON[entry.kind] || ExternalLink;
                  return (
                    <tr
                      key={i}
                      className="border-t border-border/40 hover:bg-muted/20"
                    >
                      <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">
                        {formatRelative(entry.ts)}
                      </td>
                      <td className="px-3 py-2">
                        <span className="inline-flex items-center gap-1.5 text-xs">
                          <Icon className="h-3 w-3 text-muted-foreground" />
                          {KIND_LABEL[entry.kind] || entry.kind}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-[260px]">
                        {entry.targetHost || entry.targetUrl || "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Footer note */}
      <p className="text-xs text-muted-foreground border-t border-border/60 pt-4">
        Counters and the last 100 clicks live in Upstash Redis, updated on
        every outbound click via{" "}
        <code className="font-mono text-[10px] bg-muted px-1 py-0.5 rounded">
          /api/click
        </code>
        . Affiliate IDs (Booking.com, Expedia, Hotels.com) plug in via env
        vars — see{" "}
        <code className="font-mono text-[10px] bg-muted px-1 py-0.5 rounded">
          api/click.js
        </code>
        .
      </p>
    </div>
  );
};

// What the app's model calls cost over the last seven days. Estimates from
// token counts at list prices; the Anthropic Console is the bill of record.
const LlmSpend = ({ llm }: { llm: LlmUsage | null }) => {
  const routes = llm
    ? Object.entries(llm.routes).filter(([, r]) => r.calls > 0)
    : [];
  const share = llm?.concierge.cacheReadShare;
  return (
    <section>
      <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
        Model spend, last 7 days
      </h2>
      {!llm || routes.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No model calls recorded yet. Recording started on 28 Oct 2026.
        </p>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="border border-border/60 rounded-xl p-4">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Estimated spend</div>
              <div className="text-2xl font-serif font-bold mt-1">{usd(llm.totalUsd)}</div>
            </div>
            <div className="border border-border/60 rounded-xl p-4">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Per concierge message</div>
              <div className="text-2xl font-serif font-bold mt-1">{usd(llm.concierge.medianUsd)}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                median of {llm.concierge.messages}, 90th percentile {usd(llm.concierge.p90Usd)}
              </div>
            </div>
            <div className="border border-border/60 rounded-xl p-4">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Concierge input from cache</div>
              <div className="text-2xl font-serif font-bold mt-1">
                {share == null ? "n/a" : `${Math.round(share * 100)}%`}
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5">healthy loops read most of their input from cache</div>
            </div>
            <div className="border border-border/60 rounded-xl p-4">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Web searches</div>
              <div className="text-2xl font-serif font-bold mt-1">
                {routes.reduce((s, [, r]) => s + r.web_searches, 0)}
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5">$0.01 each, plus their tokens</div>
            </div>
          </div>
          <div className="border border-border/60 rounded-xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Feature</th>
                  <th className="text-right px-3 py-2 font-medium">Requests</th>
                  <th className="text-right px-3 py-2 font-medium">Model calls</th>
                  <th className="text-right px-3 py-2 font-medium">Input</th>
                  <th className="text-right px-3 py-2 font-medium">Cache read</th>
                  <th className="text-right px-3 py-2 font-medium">Cache write</th>
                  <th className="text-right px-3 py-2 font-medium">Output</th>
                  <th className="text-right px-3 py-2 font-medium">Est. cost</th>
                </tr>
              </thead>
              <tbody className="font-mono text-xs tabular-nums">
                {routes.map(([route, r]) => (
                  <tr key={route} className="border-t border-border/40">
                    <td className="px-3 py-2 font-sans text-sm">{LLM_ROUTE_LABEL[route] || route}</td>
                    <td className="px-3 py-2 text-right">{tokens(r.calls)}</td>
                    <td className="px-3 py-2 text-right">{tokens(r.iterations)}</td>
                    <td className="px-3 py-2 text-right">{tokens(r.input)}</td>
                    <td className="px-3 py-2 text-right">{tokens(r.cache_read)}</td>
                    <td className="px-3 py-2 text-right">{tokens(r.cache_write)}</td>
                    <td className="px-3 py-2 text-right">{tokens(r.output)}</td>
                    <td className="px-3 py-2 text-right">{usd(r.usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground">
            Estimated from recorded token counts at list prices since{" "}
            {/* The window starts seven days back, but recording began 28 Oct. */}
            {llm.since > "2026-10-28" ? llm.since : "2026-10-28"}. The Anthropic
            Console is the bill of record.
          </p>
        </div>
      )}
    </section>
  );
};

const HeroNumber = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
}) => (
  <div className="border border-border/60 rounded-xl p-4">
    <div className="text-muted-foreground flex items-center gap-1.5 text-[11px] uppercase tracking-wide">
      {icon}
      {label}
    </div>
    <div className="text-3xl font-serif font-bold mt-1">{value}</div>
  </div>
);

export default Stats;
